<template>
  <div class="flex h-full items-center justify-center bg-primary-50 p-6">
    <div class="w-full max-w-[460px] bg-white rounded-lg shadow-[0_4px_40px_rgba(0,0,0,0.08)] p-8">
      <h2 class="text-xl font-semibold text-gray-800 text-center mb-6">Connect to Kairon</h2>

      <p v-if="status === 'incomplete'" data-test="connect-message" class="text-sm text-gray-700">
        This connect link is incomplete.
      </p>
      <p v-else-if="status === 'forbidden'" data-test="connect-message" class="text-sm text-gray-700">
        Only superusers can connect a key today.
      </p>
      <p v-else-if="status === 'checking'" data-test="connect-checking" class="text-sm text-gray-500">
        Checking this request...
      </p>
      <p v-else-if="status === 'error'" data-test="connect-error" class="text-sm text-red-600">
        {{ error }}
      </p>

      <template v-else>
        <p data-test="connect-copy" class="text-sm text-gray-800">
          <template v-if="client === 'cli'">
            The Kairon CLI on <strong>{{ hostLabel }}</strong> wants a key that can read and edit the library, work tickets, and use Hades if you're a superuser.
          </template>
          <template v-else>
            <strong>Kailash</strong> wants a key that can read and edit the library, work tickets, and use Hades if you're a superuser.
          </template>
          It expires in {{ expiresDays }} days.
        </p>
        <p class="mt-4 text-xs text-gray-500">Signed in as {{ email }}</p>
        <p class="text-xs text-gray-500">{{ returnsTo }}</p>
        <p v-if="error" data-test="connect-error" class="mt-4 text-sm text-red-600">{{ error }}</p>
        <div class="mt-6 flex gap-3">
          <button class="btn-primary flex-1" data-test="connect-approve" :disabled="approving" @click="approve">
            {{ approving ? 'Approving...' : 'Approve' }}
          </button>
          <button class="btn-secondary flex-1" data-test="connect-cancel" :disabled="approving" @click="cancel">
            Cancel
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { approveConnect, checkConnect } from '@/services/api/apiKeys'

const REQUIRED = ['client', 'state', 'code_challenge']
const HOSTNAME_MAX = 60
const LOOPBACK_TEXT = /localhost|127\.0\.0\.1/i

function flattenError(error) {
  const data = error?.response?.data
  if (data && typeof data === 'object') return Object.values(data).flat().map(String).join(' ')
  return 'Could not complete this request.'
}

export default {
  name: 'ConnectApprove',
  data() {
    return {
      status: 'checking',
      error: '',
      expiresDays: null,
      validated: null,
      validation: 0,
      approving: false,
    }
  },
  computed: {
    query() {
      return this.$route.query
    },
    queryKey() {
      return JSON.stringify(this.query)
    },
    client() {
      return this.query.client
    },
    hostname() {
      const hostname = this.query.hostname
      if (typeof hostname !== 'string') return undefined
      return this.isCli && LOOPBACK_TEXT.test(hostname) ? undefined : hostname
    },
    hostLabel() {
      const hostname = this.hostname?.trim()
      return hostname ? hostname.slice(0, HOSTNAME_MAX) : 'this computer'
    },
    isCli() {
      return this.client === 'cli'
    },
    returnsTo() {
      if (this.isCli) return `Returns to this computer (port ${this.query.port})`
      return `Returns to ${new URL(this.query.redirect_uri).host}`
    },
    destination() {
      return this.isCli ? { port: Number(this.query.port) } : { redirect_uri: this.query.redirect_uri }
    },
    email() {
      return this.$store.getters['auth/current_user']?.email
    },
    isComplete() {
      const required = [...REQUIRED, this.isCli ? 'port' : 'redirect_uri']
      return (
        required.every(key => typeof this.query[key] === 'string' && this.query[key]) &&
        (!this.isCli || /^\d+$/.test(this.query.port)) &&
        this.query.code_challenge_method === 'S256'
      )
    },
  },
  watch: {
    queryKey() {
      this.validate()
    },
  },
  mounted() {
    if ((localStorage.getItem('last_visited_route') || '').startsWith('/connect')) {
      localStorage.removeItem('last_visited_route')
    }
    this.validate()
  },
  methods: {
    async validate() {
      const run = ++this.validation
      this.status = 'checking'
      this.error = ''
      this.approving = false
      this.validated = null
      if (!this.isComplete) {
        this.status = 'incomplete'
        return
      }
      if (!this.$store.getters['auth/current_user']?.is_superuser) {
        this.status = 'forbidden'
        return
      }
      const request = {
        client: this.query.client,
        ...this.destination,
        state: this.query.state,
        code_challenge: this.query.code_challenge,
        code_challenge_method: this.query.code_challenge_method,
        hostname: this.hostname,
      }
      try {
        const checked = await checkConnect({ client: request.client, ...this.destination })
        if (run !== this.validation) return
        const redirectUri = this.isCli ? checked.redirect_uri : this.query.redirect_uri
        if (!redirectUri) {
          this.error = flattenError()
          this.status = 'error'
          return
        }
        this.validated = { request, redirectUri }
        this.expiresDays = checked.expires_days
        this.status = 'ready'
      } catch (error) {
        if (run !== this.validation) return
        this.error = flattenError(error)
        this.status = 'error'
      }
    },
    async approve() {
      if (this.approving || !this.validated) return
      this.approving = true
      this.error = ''
      const { request, redirectUri } = this.validated
      const run = this.validation
      try {
        const { code } = await approveConnect(request)
        if (run !== this.validation) return
        this.redirect({ code }, request.state, redirectUri)
      } catch (error) {
        if (run !== this.validation) return
        this.error = flattenError(error)
        this.approving = false
      }
    },
    cancel() {
      if (!this.validated) return
      this.redirect({ error: 'access_denied' }, this.validated.request.state, this.validated.redirectUri)
    },
    redirect(params, state, redirectUri) {
      const url = new URL(redirectUri)
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value))
      url.searchParams.set('state', state)
      this.navigate(url.toString())
    },
    navigate(url) {
      window.location.replace(url)
    },
  },
}
</script>
